import React, { useState, useEffect } from 'react';
import {
  X,
  Users2,
  Briefcase,
  MessageSquare,
  CheckCircle,
  Mail,
  User,
  Phone,
  Building,
  Star,
  Calendar,
  FileText,
  Award,
  Target,
  AlertCircle,
  Info,
  Zap,
  Save,
  Send,
  ChevronRight,
  Network,
  UserCircle,
  MapPin,
  MessageCircle,
  Linkedin
} from 'lucide-react';

const UnifiedCandidateModal = ({
  isOpen,
  onClose,
  candidate,
  onSave,
  currentUser,
  useToast,
  API_BASE_URL = 'https://candi.vdpl.co/Candidates/Candidates', // Default fallback
  fetchCandidates
}) => {
  const [activeTab, setActiveTab] = useState('reporting');
  const [sectionLoading, setSectionLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [errors, setErrors] = useState({});

  // Debug logging
  console.log('🔍 API_BASE_URL:', API_BASE_URL);
  console.log('🔍 Candidate:', candidate);

  // Get toast functions
  const { success, error: showError } = useToast || { 
    success: (msg) => alert(msg), 
    error: (msg) => alert(msg) 
  };

  // Reporting Info Fields
  const [workEmail, setWorkEmail] = useState('');
  const [reportingManagerName, setReportingManagerName] = useState('');
  const [reportingManagerEmail, setReportingManagerEmail] = useState('');
  const [reportingManagerPhone, setReportingManagerPhone] = useState('');
  const [reportingManagerConnectedDate, setReportingManagerConnectedDate] = useState('');
  const [reportingManagerLocation, setReportingManagerLocation] = useState('');
  const [reportingManagerLinkedin, setReportingManagerLinkedin] = useState('');
  const [reportingComments, setReportingComments] = useState('');
  
  // Candidate Connected Info
  const [candidateConnectedDate, setCandidateConnectedDate] = useState('');
  const [candidateConnectedComments, setCandidateConnectedComments] = useState('');
  
  // Client TAG POC Info
  const [clientTagPocName, setClientTagPocName] = useState('');
  const [clientTagPocPhone, setClientTagPocPhone] = useState('');
  const [clientTagPocEmail, setClientTagPocEmail] = useState('');
  const [clientTagPocLocation, setClientTagPocLocation] = useState('');
  const [clientTagPocLinkedin, setClientTagPocLinkedin] = useState('');
  const [clientTagPocConnectedDate, setClientTagPocConnectedDate] = useState('');
  const [clientTagPocComments, setClientTagPocComments] = useState('');
  
  // BU Name
  const [buName, setBuName] = useState('');

  // Project Details Fields
  const [projectTechStack, setProjectTechStack] = useState('');
  const [projectOverview, setProjectOverview] = useState('');
  const [projectGoals, setProjectGoals] = useState('');
  const [projectRoleResponsibility, setProjectRoleResponsibility] = useState('');
  const [keyAchievement, setKeyAchievement] = useState('');
  const [projectExperienceRating, setProjectExperienceRating] = useState(0);

  // Client Feedback Fields
  const [clientRating, setClientRating] = useState(0);
  const [clientFeedback, setClientFeedback] = useState('');
  const [improvementArea1, setImprovementArea1] = useState('');
  const [improvementArea2, setImprovementArea2] = useState('');
  const [improvementArea3, setImprovementArea3] = useState('');

  // Initialize form data when candidate changes
  useEffect(() => {
    if (candidate) {
      console.log('🔍 Initializing form with candidate data:', candidate);
      
      // Reporting Info
      setWorkEmail(candidate.work_email || candidate.workEmail || '');
      setReportingManagerName(candidate.reporting_manager_name || candidate.reportingManagerName || '');
      setReportingManagerEmail(candidate.reporting_manager_email || candidate.reportingManagerEmail || '');
      setReportingManagerPhone(candidate.reporting_manager_phone || candidate.reportingManagerPhone || '');
      const rmConnectedDate = candidate.reporting_manager_connected_date || candidate.reportingManagerConnectedDate;
      if (rmConnectedDate) {
        setReportingManagerConnectedDate(rmConnectedDate);
      } else {
        setReportingManagerConnectedDate(new Date().toISOString().split('T')[0]);
      }
      setReportingManagerLocation(candidate.reporting_manager_location || candidate.reportingManagerLocation || '');
      setReportingManagerLinkedin(candidate.reporting_manager_linkedin || candidate.reportingManagerLinkedin || '');
      setReportingComments(candidate.reporting_comments || candidate.reportingComments || '');
      
      // Candidate Connected - Set to current date if empty
      const connectedDate = candidate.candidate_connected_date || candidate.candidateConnectedDate;
      if (connectedDate) {
        setCandidateConnectedDate(connectedDate);
      } else {
        const today = new Date().toISOString().split('T')[0];
        setCandidateConnectedDate(today);
      }
      setCandidateConnectedComments(candidate.candidate_connected_comments || candidate.candidateConnectedComments || '');
      
      // Client TAG POC
      setClientTagPocName(candidate.client_tag_poc_name || candidate.clientTagPocName || '');
      setClientTagPocPhone(candidate.client_tag_poc_phone || candidate.clientTagPocPhone || '');
      setClientTagPocEmail(candidate.client_tag_poc_email || candidate.clientTagPocEmail || '');
      setClientTagPocLocation(candidate.client_tag_poc_location || candidate.clientTagPocLocation || '');
      setClientTagPocLinkedin(candidate.client_tag_poc_linkedin || candidate.clientTagPocLinkedin || '');
      
      // Client TAG POC Connected Date - Set to current date if empty
      const pocConnectedDate = candidate.client_tag_poc_connected_date || candidate.clientTagPocConnectedDate;
      if (pocConnectedDate) {
        setClientTagPocConnectedDate(pocConnectedDate);
      } else {
        const today = new Date().toISOString().split('T')[0];
        setClientTagPocConnectedDate(today);
      }
      setClientTagPocComments(candidate.client_tag_poc_comments || candidate.clientTagPocComments || '');
      
      // BU Name
      setBuName(candidate.bu_name || candidate.buName || '');

      // Project Details
      setProjectTechStack(candidate.project_tech_stack || candidate.projectTechStack || '');
      setProjectOverview(candidate.project_overview || candidate.projectOverview || '');
      setProjectGoals(candidate.project_goals || candidate.projectGoals || '');
      setProjectRoleResponsibility(candidate.project_role_responsibility || candidate.projectRoleResponsibility || '');
      setKeyAchievement(candidate.key_achievement || candidate.keyAchievement || '');
      setProjectExperienceRating(candidate.project_experience_rating || candidate.projectExperienceRating || 0);

      // Client Feedback
      setClientRating(candidate.client_rating || candidate.clientRating || 0);
      setClientFeedback(candidate.client_feedback || candidate.clientFeedback || '');
      setImprovementArea1(candidate.improvement_area_1 || candidate.improvementArea1 || '');
      setImprovementArea2(candidate.improvement_area_2 || candidate.improvementArea2 || '');
      setImprovementArea3(candidate.improvement_area_3 || candidate.improvementArea3 || '');
      
      console.log('✅ Form initialized successfully');
    }
  }, [candidate]);

  if (!isOpen) return null;

  // Validation functions
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex = /^\+?[\d\s\-().]{7,20}$/;

  const isValidPhone = (val) => !val || !val.trim() || phoneRegex.test(val.trim());
  const isValidEmail = (val) => !val || !val.trim() || emailRegex.test(val.trim());
  const isValidLinkedin = (val) => !val || !val.trim() || linkedinRegex.test(val.trim());
  const linkedinRegex = /^https:\/\/(www\.)?linkedin\.com\/in\/[A-Za-z0-9\-_%]+\/?$/;
  const urlRegex = /^https?:\/\/.+\..+/;

  const validateReporting = () => {
    const newErrors = {};

    // Work Email
    if (workEmail && !emailRegex.test(workEmail)) {
      newErrors.workEmail = 'Invalid email format (e.g. name@company.com)';
    }

    // Reporting Manager
    if (!isValidEmail(reportingManagerEmail)) {
      newErrors.reportingManagerEmail = 'Invalid email format (e.g. name@company.com)';
    }
    if (!isValidPhone(reportingManagerPhone)) {
      newErrors.reportingManagerPhone = 'Invalid phone (e.g. +1-555-0123)';
    }
    if (!isValidLinkedin(reportingManagerLinkedin)) {
      newErrors.reportingManagerLinkedin = 'Must be a valid LinkedIn URL (e.g. https://linkedin.com/in/name)';
    }

    // Client TAG POC
    if (!isValidEmail(clientTagPocEmail)) {
      newErrors.clientTagPocEmail = 'Invalid email format (e.g. name@company.com)';
    }
    if (!isValidPhone(clientTagPocPhone)) {
      newErrors.clientTagPocPhone = 'Invalid phone (e.g. +1-555-0123)';
    }
    if (!isValidLinkedin(clientTagPocLinkedin)) {
      newErrors.clientTagPocLinkedin = 'Must be a valid LinkedIn URL (e.g. https://linkedin.com/in/name)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateProject = () => {
    const newErrors = {};
    
    if (!projectTechStack.trim()) {
      newErrors.projectTechStack = 'Technology stack is required';
    }
    
    if (!projectOverview.trim()) {
      newErrors.projectOverview = 'Project overview is required';
    }
    
    if (!projectGoals.trim()) {
      newErrors.projectGoals = 'Project goals are required';
    }
    
    if (projectExperienceRating < 1 || projectExperienceRating > 5) {
      newErrors.projectExperienceRating = 'Rating must be between 1 and 5';
    }

    if (!projectRoleResponsibility.trim()) {
      newErrors.projectRoleResponsibility = 'Role and responsibility description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateFeedback = () => {
    const newErrors = {};
    
    if (clientRating < 1 || clientRating > 5) {
      newErrors.clientRating = 'Rating must be between 1 and 5';
    }
    
    if (!clientFeedback.trim()) {
      newErrors.clientFeedback = 'Client feedback is required';
    }

    if (!improvementArea1.trim()) {
      newErrors.improvementArea1 = 'First improvement area is required';
    }

    if (!improvementArea2.trim()) {
      newErrors.improvementArea2 = 'Second improvement area is required';
    }

    if (!improvementArea3.trim()) {
      newErrors.improvementArea3 = 'Third improvement area is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add history function
  const addToHistory = async (candidateId, actionType, fieldName, oldValue, newValue, reason = '', comments = '') => {
    try {
      const response = await fetch(`${API_BASE_URL}/add_candidate_history.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          candidate_id: candidateId,
          candidate_name: candidate?.candidate_name || candidate?.candidateName || 'Unknown',
          candidate_email: candidate?.candidate_email || candidate?.candidateEmail || 'unknown@email.com',
          action_type: actionType,
          field_name: fieldName,
          old_value: oldValue || 'Not set',
          new_value: newValue,
          change_reason: reason,
          change_comments: comments || `${fieldName} updated from "${oldValue || 'Not set'}" to "${newValue}"`,
          created_by: currentUser?.name || 'Current User'
        })
      });

      const result = await response.json();
      if (!result.success) {
        console.error('Failed to add history:', result.message);
      }
    } catch (error) {
      console.error('Error adding history:', error);
    }
  };

  // Send email function
  const sendEmail = async (emailType, candidateId, additionalData = {}) => {
    try {
      const payload = {
        emailType,
        candidateId,
        ...additionalData
      };
      
      const response = await fetch(`${API_BASE_URL}/send_email.php`, {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  credentials: 'include',
  body: JSON.stringify(payload)
});
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();

      if (result.success) {
        return {
          success: true,
          recipient: result.details?.recipient || additionalData.candidateEmail || 'Unknown',
          subject: result.details?.subject || 'Email Sent',
          type: emailType,
          message: result.message
        };
      } else {
        throw new Error(result.message || 'Unknown email error');
      }
    } catch (error) {
      throw new Error(error.message);
    }
  };

  // Handle save for each section
  const handleSaveSection = async (section) => {
    setSectionLoading(true);
    let isValid = false;
    let updateData = {};

    try {
      switch (section) {
        case 'reporting':
          isValid = validateReporting();
          if (isValid) {
            updateData = {
              id: candidate.id,
              enableReportingTracking: true,
              preventExtensionStatusUpdate: true,
              candidateName: candidate.candidate_name || candidate.candidateName,
              candidateEmail: candidate.candidate_email || candidate.candidateEmail
            };
            
            // Core reporting fields
            if (workEmail !== (candidate?.work_email || candidate?.workEmail || '')) updateData.workEmail = workEmail;
            if (reportingManagerName !== (candidate?.reporting_manager_name || candidate?.reportingManagerName || '')) updateData.reportingManagerName = reportingManagerName;
            if (reportingManagerEmail !== (candidate?.reporting_manager_email || candidate?.reportingManagerEmail || '')) updateData.reportingManagerEmail = reportingManagerEmail;
            if (reportingManagerPhone !== (candidate?.reporting_manager_phone || candidate?.reportingManagerPhone || '')) updateData.reportingManagerPhone = reportingManagerPhone;
            if (reportingManagerConnectedDate !== (candidate?.reporting_manager_connected_date || candidate?.reportingManagerConnectedDate || '')) updateData.reportingManagerConnectedDate = reportingManagerConnectedDate;
            if (reportingManagerLocation !== (candidate?.reporting_manager_location || candidate?.reportingManagerLocation || '')) updateData.reportingManagerLocation = reportingManagerLocation;
            if (reportingManagerLinkedin !== (candidate?.reporting_manager_linkedin || candidate?.reportingManagerLinkedin || '')) updateData.reportingManagerLinkedin = reportingManagerLinkedin;
            if (reportingComments !== (candidate?.reporting_comments || candidate?.reportingComments || '')) updateData.reportingComments = reportingComments;
            
            // Candidate Connected fields
            if (candidateConnectedDate !== (candidate?.candidate_connected_date || candidate?.candidateConnectedDate || '')) updateData.candidateConnectedDate = candidateConnectedDate;
            if (candidateConnectedComments !== (candidate?.candidate_connected_comments || candidate?.candidateConnectedComments || '')) updateData.candidateConnectedComments = candidateConnectedComments;
            
            // Client TAG POC fields
            if (clientTagPocName !== (candidate?.client_tag_poc_name || candidate?.clientTagPocName || '')) updateData.clientTagPocName = clientTagPocName;
            if (clientTagPocPhone !== (candidate?.client_tag_poc_phone || candidate?.clientTagPocPhone || '')) updateData.clientTagPocPhone = clientTagPocPhone;
            if (clientTagPocEmail !== (candidate?.client_tag_poc_email || candidate?.clientTagPocEmail || '')) updateData.clientTagPocEmail = clientTagPocEmail;
            if (clientTagPocLocation !== (candidate?.client_tag_poc_location || candidate?.clientTagPocLocation || '')) updateData.clientTagPocLocation = clientTagPocLocation;
            if (clientTagPocLinkedin !== (candidate?.client_tag_poc_linkedin || candidate?.clientTagPocLinkedin || '')) updateData.clientTagPocLinkedin = clientTagPocLinkedin;
            if (clientTagPocConnectedDate !== (candidate?.client_tag_poc_connected_date || candidate?.clientTagPocConnectedDate || '')) updateData.clientTagPocConnectedDate = clientTagPocConnectedDate;
            if (clientTagPocComments !== (candidate?.client_tag_poc_comments || candidate?.clientTagPocComments || '')) updateData.clientTagPocComments = clientTagPocComments;
            
            // BU Name
            if (buName !== (candidate?.bu_name || candidate?.buName || '')) updateData.buName = buName;
          }
          break;

        case 'project':
          isValid = validateProject();
          if (isValid) {
            updateData = {
              id: candidate.id,
              projectTechStack: projectTechStack,
              projectOverview: projectOverview,
              projectGoals: projectGoals,
              projectRoleResponsibility: projectRoleResponsibility,
              keyAchievement: keyAchievement,
              projectExperienceRating: projectExperienceRating,
              candidateName: candidate.candidate_name || candidate.candidateName,
              candidateEmail: candidate.candidate_email || candidate.candidateEmail,
              enableProjectTracking: true,
              enableProjectCompletion: true,
              enableFeedbackTracking: false,
              enableExtensionStatusCalc: false,
              enableRDForwarding: false,
              enableReportingTracking: false,
              preventExtensionStatusUpdate: true
            };

            // Add history for changed fields
            const fieldsToTrack = [
              { field: 'projectTechStack', oldValue: candidate?.project_tech_stack || '', newValue: projectTechStack, displayName: 'Project Tech Stack' },
              { field: 'projectOverview', oldValue: candidate?.project_overview || '', newValue: projectOverview, displayName: 'Project Overview' },
              { field: 'projectGoals', oldValue: candidate?.project_goals || '', newValue: projectGoals, displayName: 'Project Goals' },
              { field: 'projectRoleResponsibility', oldValue: candidate?.project_role_responsibility || '', newValue: projectRoleResponsibility, displayName: 'Project Role & Responsibility' },
              { field: 'keyAchievement', oldValue: candidate?.key_achievement || '', newValue: keyAchievement, displayName: 'Key Achievement' },
              { field: 'projectExperienceRating', oldValue: candidate?.project_experience_rating || 0, newValue: projectExperienceRating, displayName: 'Project Experience Rating' }
            ];

            for (const item of fieldsToTrack) {
              if (item.oldValue !== item.newValue && item.newValue !== '' && item.newValue !== 0) {
                await addToHistory(
                  candidate.id,
                  'project_update',
                  item.field,
                  item.oldValue,
                  item.newValue,
                  'Project details updated via Unified modal',
                  `${item.displayName} updated from "${item.oldValue || 'Not set'}" to "${item.newValue}"`
                );
              }
            }
          }
          break;

        case 'feedback':
          isValid = validateFeedback();
          if (isValid) {
            updateData = {
              id: candidate.id,
              clientRating: clientRating,
              clientFeedback: clientFeedback,
              improvementArea1: improvementArea1,
              improvementArea2: improvementArea2,
              improvementArea3: improvementArea3,
              candidateName: candidate.candidate_name || candidate.candidateName,
              candidateEmail: candidate.candidate_email || candidate.candidateEmail,
              enableFeedbackTracking: true,
              enableExtensionStatusCalc: false,
              enableRDForwarding: false,
              preventExtensionStatusUpdate: true
            };
          }
          break;
      }

      if (isValid) {
        console.log('🔍 Sending update data:', updateData);
        console.log('🔍 API URL:', `${API_BASE_URL}/update_candidate.php`);
        
        const response = await fetch(`${API_BASE_URL}/update_candidate.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            ...updateData,
            updatedBy: currentUser
              ? `${currentUser.first_name ?? ''} ${currentUser.last_name ?? ''}`.trim() || currentUser.email
              : null,
            current_user_id: currentUser?.id ?? null,
          })
        });

        console.log('🔍 Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Server error response:', errorText);
          throw new Error(`Server error: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Update result:', result);
        
        if (result.success) {
          success(`${section === 'reporting' ? 'Circle of Influence' : section === 'project' ? 'Project details' : 'Client feedback'} saved successfully!`);
          
          if (fetchCandidates) {
            await fetchCandidates();
          }
          
          setTimeout(() => {
            onClose();
          }, 1500);
        } else {
          throw new Error(result.message || 'Unknown API error');
        }
      } else {
        showError('Please fill in all required fields correctly');
      }
    } catch (err) {
      console.error('❌ Save error:', err);
      showError(`Failed to save: ${err.message}`);
    } finally {
      setSectionLoading(false);
    }
  };

  const navigationItems = [
    {
      id: 'reporting',
      label: 'Circle of Influence',
      icon: Network,
      gradient: 'from-violet-500 to-purple-600'
    },
    {
      id: 'project',
      label: 'Project Details',
      icon: Briefcase,
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      id: 'feedback',
      label: 'Client Feedback',
      icon: MessageSquare,
      gradient: 'from-orange-500 to-red-600'
    }
  ];

  // Render Circle of Influence Section
  const renderReportingSection = () => (
    <div className="space-y-4">
      {/* Hero Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 rounded-xl p-6 shadow-xl">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -mr-24 -mt-24"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-3xl -ml-16 -mb-16"></div>
        
        <div className="relative">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30 shadow-lg">
              <Network className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Circle of Influence</h2>
              <p className="text-violet-100 text-sm">Build and manage professional network connections</p>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
            <div className="flex items-start space-x-2">
              <Info className="w-4 h-4 text-white mt-0.5 flex-shrink-0" />
              <p className="text-xs text-violet-100">
                Complete once candidate has onboarded. Document all key relationships and organizational connections.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Candidate Connection Card */}
      <div className="bg-white rounded-xl border-2 border-violet-200 shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-violet-50 to-purple-50 px-5 py-3 border-b-2 border-violet-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center shadow-md">
              <UserCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Candidate Connection</h3>
              <p className="text-xs text-slate-600">Initial onboarding details</p>
            </div>
          </div>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-3 gap-4">
            {/* Connection Date - FIRST and EDITABLE */}
            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Calendar className="w-3.5 h-3.5 mr-1.5 text-violet-600" />
                Connection Date
              </label>
              <input
                type="date"
                value={candidateConnectedDate}
                onChange={(e) => setCandidateConnectedDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 transition-all"
              />
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Mail className="w-3.5 h-3.5 mr-1.5 text-violet-600" />
                Official Work Email
              </label>
              <input
                type="email"
                value={workEmail}
                onChange={(e) => setWorkEmail(e.target.value)}
                className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 transition-all ${
                  errors.workEmail ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                placeholder="candidate@company.com"
              />
              {errors.workEmail && (
                <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.workEmail}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <MessageCircle className="w-3.5 h-3.5 mr-1.5 text-violet-600" />
                Connection Notes
              </label>
              <input
                type="text"
                value={candidateConnectedComments}
                onChange={(e) => setCandidateConnectedComments(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500 transition-all"
                placeholder="Add notes..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Reporting Manager Card */}
      <div className="bg-white rounded-xl border-2 border-blue-200 shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 px-5 py-3 border-b-2 border-blue-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center shadow-md">
              <User className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Reporting Manager</h3>
              <p className="text-xs text-slate-600">Direct supervisor information</p>
            </div>
          </div>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Calendar className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                Connection Date
              </label>
              <input
                type="date"
                value={reportingManagerConnectedDate}
                onChange={(e) => setReportingManagerConnectedDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
              />
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <User className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                Manager Name
              </label>
              <input
                type="text"
                value={reportingManagerName}
                onChange={(e) => setReportingManagerName(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                placeholder="John Smith"
              />
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Mail className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                Manager Email
              </label>
              <input
                type="email"
                value={reportingManagerEmail}
                onChange={(e) => setReportingManagerEmail(e.target.value)}
                className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all ${
                  errors.reportingManagerEmail ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                placeholder="manager@company.com"
              />
              {errors.reportingManagerEmail && (
                <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.reportingManagerEmail}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Phone className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                Manager Phone
              </label>
              <input
                type="tel"
                value={reportingManagerPhone}
                onChange={(e) => setReportingManagerPhone(e.target.value)}
                className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all ${
                  errors.reportingManagerPhone ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                placeholder="+1-555-0123"
              />
              {errors.reportingManagerPhone && (
                <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.reportingManagerPhone}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <MapPin className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                Manager Location
              </label>
              <input
                type="text"
                value={reportingManagerLocation}
                onChange={(e) => setReportingManagerLocation(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                placeholder="New York, NY"
              />
            </div>

            <div className="col-span-2">
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Linkedin className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                LinkedIn URL
              </label>
              <input
                type="url"
                value={reportingManagerLinkedin}
                onChange={(e) => setReportingManagerLinkedin(e.target.value)}
                className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all ${
                  errors.reportingManagerLinkedin ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                placeholder="https://linkedin.com/in/..."
              />
              {errors.reportingManagerLinkedin && (
                <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.reportingManagerLinkedin}
                </p>
              )}
            </div>

            <div className="col-span-3">
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <MessageCircle className="w-3.5 h-3.5 mr-1.5 text-blue-600" />
                General Comments
              </label>
              <input
                type="text"
                value={reportingComments}
                onChange={(e) => setReportingComments(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all"
                placeholder="Any additional notes about reporting structure..."
              />
            </div>
          </div>
        </div>
      </div>

      {/* Client TAG POC Card */}
      <div className="bg-white rounded-xl border-2 border-emerald-200 shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-3 border-b-2 border-emerald-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md">
              <Users2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Client TAG POC</h3>
              <p className="text-xs text-slate-600">Technical account group point of contact</p>
            </div>
          </div>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-3 gap-4">
            {/* Connection Date - FIRST and EDITABLE */}
            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Calendar className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                Connection Date
              </label>
              <input
                type="date"
                value={clientTagPocConnectedDate}
                onChange={(e) => setClientTagPocConnectedDate(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
              />
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <User className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                POC Name
              </label>
              <input
                type="text"
                value={clientTagPocName}
                onChange={(e) => setClientTagPocName(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                placeholder="Jane Doe"
              />
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Mail className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                POC Email
              </label>
              <input
                type="email"
                value={clientTagPocEmail}
                onChange={(e) => setClientTagPocEmail(e.target.value)}
                className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all ${
                  errors.clientTagPocEmail ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                placeholder="poc@company.com"
              />
              {errors.clientTagPocEmail && (
                <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.clientTagPocEmail}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Phone className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                POC Phone
              </label>
              <input
                type="tel"
                value={clientTagPocPhone}
                onChange={(e) => setClientTagPocPhone(e.target.value)}
                className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all ${
                  errors.clientTagPocPhone ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                placeholder="+1-555-0123"
              />
              {errors.clientTagPocPhone && (
                <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.clientTagPocPhone}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <MapPin className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                POC Location
              </label>
              <input
                type="text"
                value={clientTagPocLocation}
                onChange={(e) => setClientTagPocLocation(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                placeholder="San Francisco"
              />
            </div>

            <div>
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <MessageCircle className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                POC Notes
              </label>
              <input
                type="text"
                value={clientTagPocComments}
                onChange={(e) => setClientTagPocComments(e.target.value)}
                className="w-full px-3 py-2 text-sm border-2 border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                placeholder="Notes..."
              />
            </div>

            <div className="col-span-3">
              <label className="flex items-center text-xs font-semibold text-slate-700 mb-1.5">
                <Linkedin className="w-3.5 h-3.5 mr-1.5 text-emerald-600" />
                LinkedIn URL
              </label>
              <input
                type="url"
                value={clientTagPocLinkedin}
                onChange={(e) => setClientTagPocLinkedin(e.target.value)}
                className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all ${
                  errors.clientTagPocLinkedin ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                placeholder="https://linkedin.com/in/..."
              />
              {errors.clientTagPocLinkedin && (
                <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.clientTagPocLinkedin}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="flex justify-end pt-2">
        <button
          onClick={() => handleSaveSection('reporting')}
          disabled={sectionLoading}
          className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-sm font-bold rounded-lg hover:from-violet-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl hover:scale-105 flex items-center space-x-2"
        >
          {sectionLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Circle of Influence</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Render Project Details Section
  const renderProjectSection = () => (
    <div className="space-y-5">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-xl border border-blue-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center">
            <Briefcase className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Project Information & Experience</h3>
        </div>
        <p className="text-slate-600 text-sm">Update current project details and technical experience</p>
        
        {/* Instructions */}
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-900 mb-0.5">Instructions</p>
              <p className="text-xs text-blue-800">
                Fill out this section with the candidate's technology stack and contributions. If unavailable, select 'Forward Form to Candidate'.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Zap className="w-4 h-4 mr-2 text-blue-600" />
            Technology Stack *
          </label>
          <p className="text-xs text-slate-600 mb-1">What are the key technologies, frameworks, or tools you're working with on this project?</p>
          <textarea
            value={projectTechStack}
            onChange={(e) => setProjectTechStack(e.target.value)}
            rows={2}
            className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all ${
              errors.projectTechStack ? 'border-red-500 bg-red-50' : 'border-slate-300'
            }`}
            placeholder="e.g., React.js, Node.js, AWS, MongoDB, Docker..."
          />
          {errors.projectTechStack && (
            <div className="flex items-center space-x-1 text-red-600 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>{errors.projectTechStack}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Briefcase className="w-4 h-4 mr-2 text-blue-600" />
            Project Overview *
          </label>
          <p className="text-xs text-slate-600 mb-1">Can you briefly describe the project and what the client is aiming to achieve?</p>
          <textarea
            value={projectOverview}
            onChange={(e) => setProjectOverview(e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all ${
              errors.projectOverview ? 'border-red-500 bg-red-50' : 'border-slate-300'
            }`}
            placeholder="Describe the project's purpose, what the client wants to accomplish, and the overall vision..."
          />
          {errors.projectOverview && (
            <div className="flex items-center space-x-1 text-red-600 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>{errors.projectOverview}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Target className="w-4 h-4 mr-2 text-blue-600" />
            Project Goals *
          </label>
          <p className="text-xs text-slate-600 mb-1">What are the specific outcomes or success metrics for this project?</p>
          <textarea
            value={projectGoals}
            onChange={(e) => setProjectGoals(e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all ${
              errors.projectGoals ? 'border-red-500 bg-red-50' : 'border-slate-300'
            }`}
            placeholder="Describe the measurable goals, deliverables, KPIs, or success criteria..."
          />
          {errors.projectGoals && (
            <div className="flex items-center space-x-1 text-red-600 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>{errors.projectGoals}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <User className="w-4 h-4 mr-2 text-blue-600" />
            Your Role & Responsibilities *
          </label>
          <p className="text-xs text-slate-600 mb-1">What is your position in the project, and what are your main responsibilities?</p>
          <textarea
            value={projectRoleResponsibility}
            onChange={(e) => setProjectRoleResponsibility(e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all ${
              errors.projectRoleResponsibility ? 'border-red-500 bg-red-50' : 'border-slate-300'
            }`}
            placeholder="Describe your job title, daily tasks, key responsibilities, and how you contribute to the project..."
          />
          {errors.projectRoleResponsibility && (
            <div className="flex items-center space-x-1 text-red-600 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>{errors.projectRoleResponsibility}</span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Award className="w-4 h-4 mr-2 text-blue-600" />
            Key Achievement or Success Story (Optional)
          </label>
          <p className="text-xs text-slate-600 mb-1">Have you made any notable contributions or reached any significant milestones?</p>
          <textarea
            value={keyAchievement}
            onChange={(e) => setKeyAchievement(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all border-slate-300"
            placeholder="Share any notable accomplishments, problem-solving successes, or positive impact..."
          />
        </div>

        <div className="space-y-2">
          <label className="flex items-center text-sm font-semibold text-slate-700">
            <Star className="w-4 h-4 mr-2 text-blue-600" />
            Project Experience Rating *
          </label>
          <p className="text-xs text-slate-600 mb-1">How would you rate your overall experience working on this project?</p>
          <div className="flex items-center space-x-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setProjectExperienceRating(star)}
                className={`p-1 ${star <= projectExperienceRating ? 'text-yellow-400' : 'text-slate-300'} hover:text-yellow-400 transition-colors`}
              >
                <Star className="w-6 h-6 fill-current" />
              </button>
            ))}
            <span className="ml-2 text-sm text-slate-600">
              {projectExperienceRating > 0 ? `${projectExperienceRating}/5` : 'No rating'}
            </span>
          </div>
          {errors.projectExperienceRating && (
            <div className="flex items-center space-x-1 text-red-600 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>{errors.projectExperienceRating}</span>
            </div>
          )}
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-4 border-t border-slate-200">
        <button
          onClick={async () => {
            try {
              setEmailSending(true);
              
              await sendEmail('project_info_form', candidate.id, {
                candidateName: candidate.candidate_name || candidate.candidateName,
                candidateEmail: candidate.candidate_email || candidate.candidateEmail,
                client: candidate.client,
                jobTitle: candidate.job_title || candidate.jobTitle,
                formSentAt: new Date().toISOString().slice(0, 19).replace('T', ' ')
              });
              
              const currentDateTime = new Date().toISOString().slice(0, 19).replace('T', ' ');
              
              await fetch(`${API_BASE_URL}/update_candidate.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: candidate.id,
                  project_form_sent_at: currentDateTime,
                  project_form_reminder_count: 0
                })
              });
              
              success(`Project information form sent successfully to ${candidate.candidate_name || candidate.candidateName}!`);
              if (fetchCandidates) await fetchCandidates();
              
            } catch (err) {
              showError(`Failed to send project information form: ${err.message}`);
            } finally {
              setEmailSending(false);
            }
          }}
          disabled={sectionLoading || emailSending}
          className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl flex items-center space-x-2"
        >
          {emailSending ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Sending...</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>Forward Form to Candidate</span>
            </>
          )}
        </button>
        
        <button
          onClick={() => handleSaveSection('project')}
          disabled={sectionLoading || emailSending}
          className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
        >
          {sectionLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Project Information</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Render Client Feedback Section
  const renderFeedbackSection = () => (
    <div className="space-y-5">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-red-50 to-pink-50 p-5 rounded-xl border border-red-200">
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-r from-red-600 to-pink-600 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">Client Feedback Collection</h3>
        </div>
        <p className="text-slate-600 text-sm">Collect and document client performance feedback</p>
        
        {/* Instructions */}
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-blue-900 mb-0.5">Instructions</p>
              <p className="text-xs text-blue-800">
                Ask the client: "On a scale of 1 to 5, how would you rate the overall performance of the candidate?"
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Client Rating */}
      <div className="space-y-2">
        <label className="flex items-center text-sm font-semibold text-slate-700">
          <CheckCircle className="w-4 h-4 mr-2 text-red-600" />
          Client Rating (1-5 stars) *
        </label>
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setClientRating(star)}
                disabled={sectionLoading}
                className={`p-2 rounded-lg transition-all hover:scale-110 ${
                  star <= clientRating 
                    ? 'text-yellow-400 bg-yellow-50 hover:bg-yellow-100' 
                    : 'text-slate-300 hover:text-yellow-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Star className={`w-6 h-6 ${star <= clientRating ? 'fill-yellow-400' : ''}`} />
              </button>
            ))}
            <div className="ml-3 px-3 py-2 bg-slate-100 rounded-lg border border-slate-200">
              <span className="text-sm font-medium text-slate-700">
                {clientRating > 0 ? (
                  <div className="flex items-center space-x-2">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    <span>{clientRating}/5</span>
                    <span className="text-xs text-slate-500">
                      ({clientRating >= 4 ? 'Excellent' : clientRating >= 3 ? 'Good' : clientRating >= 2 ? 'Fair' : 'Poor'})
                    </span>
                  </div>
                ) : (
                  'No rating'
                )}
              </span>
            </div>
          </div>
        </div>
        {errors.clientRating && (
          <div className="flex items-center space-x-1 text-red-600 text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>{errors.clientRating}</span>
          </div>
        )}
      </div>

      {/* 3 Key Areas for Improvement */}
      <div className="space-y-2">
        <label className="flex items-center text-sm font-semibold text-slate-700">
          <Target className="w-4 h-4 mr-2 text-red-600" />
          3 Key Areas for Improvement *
        </label>
        <div className="bg-blue-50 p-2.5 rounded-lg border border-blue-200 mb-2">
          <div className="flex items-start space-x-2">
            <Info className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-800">
              Identify the top 3 areas where the candidate can improve their performance.
            </p>
          </div>
        </div>
        
        <div className="space-y-2.5">
          {[
            { value: improvementArea1, setter: setImprovementArea1, error: 'improvementArea1', placeholder: 'e.g., Communication with team members' },
            { value: improvementArea2, setter: setImprovementArea2, error: 'improvementArea2', placeholder: 'e.g., Time management and meeting deadlines' },
            { value: improvementArea3, setter: setImprovementArea3, error: 'improvementArea3', placeholder: 'e.g., Technical documentation skills' }
          ].map((area, index) => (
            <div key={index} className="flex items-center space-x-2.5">
              <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-red-600">{index + 1}</span>
              </div>
              <input
                type="text"
                value={area.value}
                onChange={(e) => area.setter(e.target.value)}
                disabled={sectionLoading}
                className={`flex-1 px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all disabled:opacity-50 ${
                  errors[area.error] ? 'border-red-500 bg-red-50' : 'border-slate-300'
                }`}
                placeholder={area.placeholder}
              />
            </div>
          ))}
        </div>
        
        {(errors.improvementArea1 || errors.improvementArea2 || errors.improvementArea3) && (
          <div className="flex items-center space-x-1 text-red-600 text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>Please fill in all 3 areas for improvement</span>
          </div>
        )}
      </div>

      {/* Client Feedback Comments */}
      <div className="space-y-2">
        <label className="flex items-center text-sm font-semibold text-slate-700">
          <FileText className="w-4 h-4 mr-2 text-red-600" />
          Client Feedback Comments *
        </label>
        <textarea
          value={clientFeedback}
          onChange={(e) => setClientFeedback(e.target.value)}
          disabled={sectionLoading}
          rows={3}
          className={`w-full px-3 py-2 text-sm border-2 rounded-lg focus:ring-2 focus:ring-red-500/30 focus:border-red-500 transition-all disabled:opacity-50 ${
            errors.clientFeedback ? 'border-red-500 bg-red-50' : 'border-slate-300'
          }`}
          placeholder="Enter detailed client feedback about the candidate's performance, strengths, and overall satisfaction..."
        />
        {errors.clientFeedback && (
          <div className="flex items-center space-x-1 text-red-600 text-xs">
            <AlertCircle className="w-3 h-3" />
            <span>{errors.clientFeedback}</span>
          </div>
        )}
      </div>

      {/* Action Button */}
      <div className="flex justify-end pt-4 border-t border-slate-200">
        <button
          onClick={() => handleSaveSection('feedback')}
          disabled={sectionLoading}
          className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 text-white text-sm rounded-lg hover:from-red-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-lg hover:shadow-xl flex items-center space-x-2"
        >
          {sectionLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Client Feedback</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex h-full items-center justify-center p-4">
        <div className="relative w-full max-w-7xl h-[92vh] bg-white rounded-2xl shadow-2xl transform transition-all flex overflow-hidden">
          
          {/* Left Sidebar Navigation */}
          <div className="w-72 bg-gradient-to-br from-slate-50 to-slate-100 border-r border-slate-200 flex flex-col">
            {/* Header - More Compact */}
            <div className="p-4 border-b border-slate-200">
              <h2 className="text-base font-semibold text-slate-700 mb-3">
                Candidate Management
              </h2>
              <div className="space-y-1">
                <p className="text-base font-bold text-slate-900">
                  {candidate?.candidate_name || candidate?.candidateName || 'N/A'}
                </p>
                <p className="text-xs text-slate-700">
                  <span className="font-semibold">Job Title:</span> {candidate?.job_title || candidate?.jobTitle || 'N/A'}
                </p>
                <p className="text-xs text-slate-700">
                  <span className="font-semibold">Client:</span> {candidate?.client || 'N/A'}
                </p>
                <p className="text-xs text-slate-700">
                  <span className="font-semibold">BU Head:</span> {candidate?.bu_head || candidate?.buHead || 'N/A'}
                </p>
              </div>
            </div>

            {/* Navigation Items - Removed descriptions and spacing */}
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all duration-200 group ${
                      isActive
                        ? `bg-gradient-to-r ${item.gradient} shadow-md`
                        : 'bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2.5">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                        isActive
                          ? 'bg-white/20'
                          : `bg-gradient-to-br ${item.gradient}`
                      }`}>
                        <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-white'}`} />
                      </div>
                      <h3 className={`font-semibold text-sm flex-1 ${
                        isActive ? 'text-white' : 'text-slate-900'
                      }`}>
                        {item.label}
                      </h3>
                      {isActive && (
                        <ChevronRight className="w-4 h-4 text-white" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-slate-200">
              <div className="bg-blue-50 rounded-lg p-2.5 border border-blue-200">
                <div className="flex items-start space-x-2">
                  <Info className="w-3.5 h-3.5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-800">
                    Complete all sections to maintain comprehensive candidate records.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Top Bar */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  {navigationItems.find(item => item.id === activeTab)?.label}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto px-6 py-5 bg-slate-50">
              {activeTab === 'reporting' && renderReportingSection()}
              {activeTab === 'project' && renderProjectSection()}
              {activeTab === 'feedback' && renderFeedbackSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedCandidateModal;